/**
 * Construcción de entries del audit trail — función pura.
 *
 * Recibe el input parcial del caller + el contexto (usuario activo, sesión,
 * última entry de la cadena) y devuelve la entry completa con hashes
 * calculados, lista para persistir.
 *
 * Los efectos colaterales (push al array, save a storage, emitir eventos)
 * los maneja el caller — este módulo se mantiene puro y testeable.
 */

import type { AuditEntry, AuditEntryInput, Usuario } from '../types';
import { INITIAL_HASH, hashAuditEntry } from './hash-chain';

export interface BuildAuditEntryContext {
  /** Usuario que está ejecutando la acción. `null` si no hay sesión. */
  usuario: Usuario | null;
  /** Login timestamp de la sesión activa — entra en `metadatos.sesionId`. */
  sesionLoginAt: string | null;
  /** Última entry de la cadena (para encadenar). `null` si la cadena está vacía. */
  prevEntry: AuditEntry | null;
  /**
   * Inyectables para testabilidad (Date.now y user-agent string).
   * En producción: `new Date().toISOString()` y `navigator.userAgent`.
   */
  now?: () => Date;
  userAgent?: string | null;
  /** Counter del trail (se incrementa antes de construir el ID). */
  nextCounter: number;
}

/**
 * Construye una entry completa con hashes. NO la persiste — eso lo hace el caller.
 *
 * Garantías:
 *   - `hashPrev` siempre matchea el `hashCurrent` de `prevEntry` (o INITIAL_HASH si null)
 *   - `hashCurrent` se calcula con `hashAuditEntry` (incluye `hashPrev` en payload)
 *   - Campos opcionales del input ausentes quedan en `null` (no `undefined`)
 */
export function buildAuditEntry(
  input: AuditEntryInput,
  ctx: BuildAuditEntryContext,
): AuditEntry {
  const now = ctx.now ?? (() => new Date());
  const id = `AT-${String(ctx.nextCounter).padStart(6, '0')}`;
  const prevHash = ctx.prevEntry ? ctx.prevEntry.hashCurrent : INITIAL_HASH;

  const entry: AuditEntry = {
    id,
    timestamp: now().toISOString(),
    usuarioId: ctx.usuario?.id ?? null,
    usuarioNombreSnap: ctx.usuario?.nombre ?? '(sin sesión)',
    ubicacionId: ctx.usuario?.ubicacionId ?? null,
    rol: ctx.usuario?.rol ?? null,
    categoria: input.categoria,
    accion: input.accion,
    entidadTipo: input.entidadTipo ?? null,
    entidadId: input.entidadId ?? null,
    valoresAntes: input.valoresAntes ?? null,
    valoresDespues: input.valoresDespues ?? null,
    razon: input.razon ?? null,
    significadoFirma: input.significadoFirma ?? null,
    detalle: input.detalle ?? null,
    metadatos: {
      sesionId: ctx.sesionLoginAt,
      userAgent: ctx.userAgent ?? null,
    },
    hashPrev: prevHash,
    hashCurrent: '', // placeholder — se completa abajo
  };

  entry.hashCurrent = hashAuditEntry(entry, prevHash);
  return entry;
}
